import ModalTittle from "@View/components/ModalTittle";
import ModalWrapper from "@View/components/ModalWrapper";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Tabs, Tab, Box } from "@mui/material";
import ButtonSave from "@View/components/buttons/ButtonSave";
import ButtonBB from "@View/components/buttons/ButtonBB";
import getAPI from "@API/index";
import { getRandomKey } from "@Utils/math";
import { AppActions } from "@State/reducers/getReducers/appReducer";
import RKKRatingTab from "./SDMComponents/RKK/RKKRatingTab";
import RKKDtCtTab from "./SDMComponents/RKK/RKKDtCtTab";
import RKKCommentsTab from "./SDMComponents/RKK/RKKCommentsTab";

const EMPTY_FORM_DATA = {
  rating: {
    sphereOfActivity: "",
    isOffshoreResident: "",
    bankLimitCapital: "",
    quarters: [],
    commentsToFinalIndicators: "",
    commentsToAdjustments: "",
    commentsToGeneralFinalIndicators: "",
    preparedBy: "",
    checkedBy: "",
    documentDate: "",
  },
  dtCt: {
    debitorsComment: "",
    creditorsComment: "",
  },
  comments: {
    balanceComment: "",
    pnLComment: "",
  },
};

const tabs = [
  { label: "РКК", key: "rkk" },
  { label: "Дт-Кт", key: "dtct" },
  { label: "Комментарии к отчетности", key: "comments" },
];

/**
 * @param {{
 *   handleClose: () => void,
 *   clientId?: string,
 *   clientName?: string,
 *   customerId?: string,
 *   consolidationId?: string,
 * }} props
 */
const SDMClientRKK = ({ handleClose, clientId, clientName, customerId, consolidationId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  const isConsolidationMode = Boolean(consolidationId && customerId);

  const apiPaths = isConsolidationMode
    ? {
        data: `/api/customer/${customerId}/consolidation/${consolidationId}/rating-rkk/data`,
        recalc: `/api/customer/${customerId}/consolidation/${consolidationId}/rating-rkk/recalculate`,
        file: `/api/customer/${customerId}/consolidation/${consolidationId}/rating-rkk/file`,
      }
    : {
        data: `/api/client/${clientId}/rating-rkk/v2/data`,
        recalc: `/api/client/${clientId}/rating-rkk/v2/recalculate`,
        file: `/api/client/${clientId}/rating-rkk/v2/file`,
      };

  const loadData = async () => {
    const key = getRandomKey();
    try {
      setLoading(true);
      dispatch(AppActions.startLoading(key));

      const response = await getAPI().request({
        url: apiPaths.data,
        method: "get",
      });

      if (response?.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error("[SDMClientRKK] loadData", error);
    } finally {
      setLoading(false);
      dispatch(AppActions.endLoading(key));
    }
  };

  useEffect(() => {
    loadData();
  }, [clientId, consolidationId, customerId]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const updateFormData = (path, value) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const nextKey = keys[i + 1];
        if (!isNaN(nextKey)) {
          if (!current[key]) current[key] = [];
          current = current[key];
        } else {
          if (!current[key]) current[key] = {};
          current = current[key];
        }
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleSave = async () => {
    const key = getRandomKey();
    try {
      dispatch(AppActions.startLoading(key));

      const response = await getAPI().request({
        url: apiPaths.data,
        method: "put",
        data: formData,
      });

      if (response?.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error("[SDMClientRKK] handleSave", error);
    } finally {
      dispatch(AppActions.endLoading(key));
    }
  };

  const handleRecalculate = async () => {
    const key = getRandomKey();
    try {
      dispatch(AppActions.startLoading(key));

      const response = await getAPI().request({
        url: apiPaths.recalc,
        method: "post",
        data: formData,
      });

      if (response?.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error("[SDMClientRKK] handleRecalculate", error);
    } finally {
      dispatch(AppActions.endLoading(key));
    }
  };

  const handleDownloadFile = async () => {
    try {
      await handleSave();

      const fileSuffix = isConsolidationMode ? consolidationId : clientId;
      const fileSettings = { name: `РКК_рейтинг_${fileSuffix}.xlsx` };
      const blob = await getAPI().request({
        url: apiPaths.file,
        method: "get",
        responseType: "blob",
        _fileSettings: fileSettings,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileSettings.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[SDMClientRKK] handleDownloadFile", error);
    }
  };

  if (loading) {
    return (
      <ModalWrapper maxWidth={"1200px"} open={true} handleClose={handleClose} btnTxt={"Close"}>
        <Box sx={{ padding: "40px", textAlign: "center" }}>Loading...</Box>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper maxWidth={"1500px"} open={true} handleClose={handleClose} btnTxt={"Close"}>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(90vh - 60px)",
        margin: "-30px",
        padding: 0,
      }}>
        <Box sx={{ flexShrink: 0, padding: "30px 30px 0px" }}>
          <ModalTittle fontSize={"20px"} children2={clientName ? `${clientName}` : "Рейтинг РКК"} marginBottom={"10px"} />
        </Box>

        <Box sx={{
          borderBottom: "1px solid #E1E6F1",
          padding: "0 30px",
          flexShrink: 0,
          "& .MuiTabs-indicator": { backgroundColor: "#354659" },
          "& .MuiTab-root": {
            color: "#54606F",
            fontWeight: 500,
            fontSize: "16px",
            textTransform: "none",
            "&.Mui-selected": { color: "#1A1E24", fontWeight: 600 },
            "&:hover": { color: "#1A1E24" },
          },
        }}>
          <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {tabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{
          flex: 1,
          overflowY: "scroll",
          padding: "20px 30px 0",
          minHeight: 0,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
            "&:hover": { backgroundColor: "#555" },
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#888 #f1f1f1",
        }}>
          {tabs[selectedTab]?.key === "rkk" ? (
            <RKKRatingTab data={formData} onUpdate={updateFormData} />
          ) : tabs[selectedTab]?.key === "dtct" ? (
            <RKKDtCtTab data={formData} onUpdate={updateFormData} />
          ) : tabs[selectedTab]?.key === "comments" ? (
            <RKKCommentsTab data={formData} onUpdate={updateFormData} />
          ) : null}
        </Box>

        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          padding: "16px 30px 30px",
          flexShrink: 0,
          borderTop: "1px solid #E1E6F1",
        }}>
          <div style={{ marginTop: "-20px" }}>
            <ButtonSave onClick={handleRecalculate} marginTop={0} marginBottom={0}>
              Пересчитать
            </ButtonSave>
          </div>

          <Box sx={{ display: "flex", gap: 1 }}>
            <div style={{ marginTop: "-20px" }}>
              <ButtonSave onClick={handleSave} marginTop={0} marginBottom={0}>
                {t("buttons.Save")}
              </ButtonSave>
            </div>
            <ButtonBB onClick={handleDownloadFile} className="ButtonAddNew" style={{ padding: "15px 20px" }}>
              {t("buttons.ExportToExcel")}
            </ButtonBB>
          </Box>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default SDMClientRKK;
